import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyleSheet,
  ActivityIndicator,
  Image,
  Platform,
  Dimensions,
  TouchableOpacity,
  Linking,
} from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/RestyleIcon';
import useActivityLogsForTasks from '@hooks/useActivityLogsForTasks';
import { ActivityLogMobile } from '@screens/Projects/Report/components/ActivityLogMobile';
import theme from '@themes/theme';

import type { ReactNode } from 'react';

export enum ProjectActivityType {
  TaskDocument = 'TaskDocument',
  ProjectDocument = 'ProjectDocument',
  ProjectCreated = 'ProjectCreated',
  ProjectAssigned = 'ProjectAssigned',
  ProjectUnassigned = 'ProjectUnassigned',
  ProjectUnassignedSelf = 'ProjectUnassignedSelf',
  ProjectCompleted = 'ProjectCompleted',
  MessageSent = 'MessageSent',
  TaskCreated = 'TaskCreated',
  TaskAssigned = 'TaskAssigned',
  TaskUnassigned = 'TaskUnassigned',
  TaskUnassignedSelf = 'TaskUnassignedSelf',
  TaskCompleted = 'TaskCompleted',
  TaskCompletedRestore = 'TaskCompletedRestore',
  Default = '',
}

export const getProjectActivityMessage = (
  event: ProjectActivityType,
  projectRole?: string,
  role?: string,
  messageBody?: string
) => {
  const result: {
    message: string;
    projectRole?: string;
    role?: string;
    messageBody?: string;
  } = { message: '' };

  switch (event) {
    case ProjectActivityType.ProjectCreated:
      result.message = 'Created this project';
      break;
    case ProjectActivityType.ProjectAssigned:
      result.message = `Added as Project ${projectRole || ''}`;
      result.projectRole = projectRole;
      break;
    case ProjectActivityType.ProjectUnassigned:
      result.message = 'Unassigned from this project';
      break;
    case ProjectActivityType.ProjectUnassignedSelf:
      result.message = 'Left this Project';
      break;
    case ProjectActivityType.TaskCompleted:
      result.message = 'Completed this task';
      break;
    case ProjectActivityType.TaskAssigned:
      result.message = `Added as Task ${role || ''}`;
      result.role = role;
      break;
    case ProjectActivityType.TaskCreated:
      result.message = 'Created this task';
      break;
    case ProjectActivityType.TaskCompletedRestore:
      result.message = 'Restored this task';
      break;
    case ProjectActivityType.TaskUnassigned:
      result.message = 'Unassigned from this task';
      break;
    case ProjectActivityType.TaskUnassignedSelf:
      result.message = 'Left this task';
      break;
    case ProjectActivityType.ProjectDocument:
      result.message = 'Added file to this project';
      break;
    case ProjectActivityType.TaskDocument:
      result.message = 'Added file to this task';
      break;
    case ProjectActivityType.MessageSent:
      result.message = messageBody || '';
      result.messageBody = messageBody;
      break;
    default:
      result.message = '';
  }

  return result;
};

interface Task {
  description: ReactNode;
  id: string;
  name: string;
  creator?: {
    id: string;
    name: string;
    avatar?: {
      url: string;
    };
  };
  startDate?: string;
  dueDate?: string;
  status?: string;
  priority?: string;
}

interface TasksProp {
  listProjectReportTasks?: Task[];
}

interface ImagesType {
  file: {
    thumbnail: string;
    url: string;
    cdnBaseUrl: string;
    path: string;
  };
}

interface LogTypes {
  user: {
    name: string;
  };
  createdAt: string;
  event: ProjectActivityType;
  attachment: {
    body: string;
  };
}

const getStatus = (status: string) => {
  return status
    ?.split('_')
    .map(
      (word: string) =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
};

const TaskDetailMobile = ({
  t,
  task,
}: {
  t: (key: string) => string;
  task: Task;
}) => (
  <Box justifyContent='space-between'>
    <Box flexDirection='row'>
      <Box justifyContent='center' mx='m'>
        {task?.creator?.avatar?.url ? (
          <Image
            style={{
              width: 38,
              height: 38,
              borderRadius: 30,
            }}
            source={{ uri: task?.creator?.avatar?.url }}
          />
        ) : (
          <Avatar
            onPress={() => {}}
            id={task?.creator?.id}
            avatar={null}
            label={task?.creator?.name}
            size='small'
          />
        )}
      </Box>

      <Box>
        <Text color='textPrimary' fontWeight='700' fontSize={14}>
          {t('models:projects.report.overview.projectDescription.owner')}
        </Text>
        <Text color='textPrimary' fontWeight='500' fontSize={14}>
          {task?.creator?.name}
        </Text>
      </Box>
    </Box>
    <Box flexDirection='row' marginVertical='m' justifyContent='space-evenly'>
      <Box>
        <Text
          marginLeft='xs'
          color='textPrimary'
          fontWeight='700'
          fontSize={14}>
          {t('models:projects.report.overview.taskOverview.startDate')}
        </Text>
        <Text color='textPrimary' fontWeight='500' fontSize={14}>
          {format(
            task?.startDate || '2025-03-12T05:30:00+05:30',
            'MMM dd, yyyy'
          )}
        </Text>
      </Box>
      <Box marginHorizontal='m' style={styles.line}></Box>
      <Box>
        <Text
          marginLeft='xs'
          color='textPrimary'
          fontWeight='700'
          fontSize={14}>
          {t('models:projects.report.overview.taskOverview.dueDate')}
        </Text>
        <Text color='textPrimary' fontWeight='500' fontSize={14}>
          {task?.dueDate
            ? format(
                task?.dueDate || '2025-03-12T05:30:00+05:30',
                'MMM dd, yyyy'
              )
            : ''}
        </Text>
      </Box>
      <Box marginHorizontal='m' style={styles.line}></Box>
      <Box>
        <Text
          marginLeft='xs'
          color='textPrimary'
          fontWeight='700'
          fontSize={14}>
          {t('models:projects.report.overview.taskOverview.status')}
        </Text>
        <Text color='textPrimary' fontWeight='500' fontSize={14}>
          {task?.status}
        </Text>
      </Box>
    </Box>
  </Box>
);

const TaskHeaderMobile = ({ task }: { task: Task }) => (
  <Box marginVertical='s'>
    <Box>
      <Text
        variant='heading1'
        color='textPrimary'
        fontWeight='600'
        fontSize={28}
        textAlign='left'
        marginVertical='xs'>
        Task: {task?.name}
      </Text>
    </Box>

    <Box flexDirection='row' alignSelf='flex-end'>
      <Box justifyContent='center'>
        {task?.priority === 'HIGH' && (
          <Icon name='StatusHigh' variant='xl' color='textPrimary' />
        )}
        {task?.priority === 'MEDIUM' && (
          <Icon name='StatusMedium' variant='xl' color='textPrimary' />
        )}
      </Box>
      <Box justifyContent='center'>
        {task?.priority === 'MEDIUM' || task?.priority === 'HIGH' ? (
          <Text
            style={{ textTransform: 'capitalize' }}
            color='black'
            fontWeight='500'
            fontSize={12}
            textAlign='left'
            marginVertical='xs'>
            {task?.priority.toLowerCase()}
            {' Priority'}
          </Text>
        ) : null}
      </Box>
    </Box>
  </Box>
);

const cleanText = (text: string) => {
  return text.replace(/[{}\[\]\(\)\d]/g, '').trim();
};

const getRole = (log) => {
  const role =
    log?.attachment?.entityTask?.members
      ?.find(
        (member: { user: { id: string } }) => member?.user?.id === log?.user?.id
      )
      ?.role.toLowerCase() || '';

  return role;
};

const ImagesComponent = (images: ImagesType, log) => {
  if (images?.file?.url) {
    return (
      <Box key={images?.file?.url} margin='l'>
        <TouchableOpacity
          onPress={() =>
            Linking.openURL(`${images?.file?.cdnBaseUrl}/${images?.file?.path}`)
          }>
          <Box>
            <Image
              style={{
                height: 200,
                width: 330,
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
              }}
              source={{
                uri:
                  images?.file?.thumbnail ??
                  `${images?.file?.cdnBaseUrl}/${images?.file?.path}`,
              }}
            />
          </Box>
        </TouchableOpacity>

        <Box
          width={330}
          height='auto'
          paddingVertical='xxs'
          paddingHorizontal='s'
          marginBottom='s'
          style={{
            backgroundColor: '#F7F8FA',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
          }}>
          <Box flex={1} flexDirection='row' justifyContent='space-between'>
            <Text
              variant='labelSmall'
              fontSize={14}
              fontWeight='500'
              marginRight='xxs'
              style={{ marginTop: 2 }}>
              {log?.user?.name ?? ''}
            </Text>
            <Text
              fontSize={14}
              fontWeight='500'
              style={{
                color: 'grey04',
                opacity: 0.5,
              }}>
              {`${format(
                log?.attachment?.createdAt ?? '2025-03-12T05:30:00+05:30',
                'MMM dd, yyyy'
              )} at ${format(
                log?.attachment?.createdAt ?? '2025-03-12T05:30:00+05:30',
                'hh:mm aa'
              )}`}
            </Text>
          </Box>
          <Text fontSize={14} fontWeight='700'>
            {cleanText(log?.attachment?.body ?? '')}
          </Text>
        </Box>
      </Box>
    );
  }
  return null;
};

type CreateTaskItemArgs = {
  getRole: (log: LogTypes) => string;
  imagesComponent: (images: ImagesType, log: LogTypes) => ReactNode;
};

const createTaskItem =
  ({ getRole, imagesComponent }: CreateTaskItemArgs) =>
  ({
    task,
    t,
    taskActivityLogs = [],
  }: {
    task: Task;
    t: (key: string) => string;
    taskActivityLogs: unknown[];
  }) => (
    <Box marginHorizontal='s'>
      {/** Task Header */}
      {Platform.OS === 'web' ? (
        <Box
          marginVertical={Platform.OS === 'web' ? 'm' : 'xxs'}
          flexDirection='row'
          justifyContent='space-between'
          flexWrap='wrap'>
          <Box>
            <Text
              variant='heading1'
              color='textPrimary'
              fontWeight='600'
              fontSize={28}
              textAlign='left'
              marginVertical='xs'>
              Task: {task?.name}
            </Text>
          </Box>

          <Box flexDirection='row'>
            <Box justifyContent='center'>
              {task?.priority === 'HIGH' && (
                <Icon name='StatusHigh' variant='xl' color='textPrimary' />
              )}
              {task?.priority === 'MEDIUM' && (
                <Icon name='StatusMedium' variant='xl' color='textPrimary' />
              )}
            </Box>
            <Box justifyContent='center'>
              {task?.priority === 'HIGH' || task?.priority === 'MEDIUM' ? (
                <Text
                  variant='metadataSecondary'
                  style={{ textTransform: 'capitalize' }}
                  color='black'
                  fontWeight='500'
                  fontSize={10}
                  textAlign='left'
                  marginVertical='xs'>
                  {task?.priority.toLowerCase()}
                  {' Priority'}
                </Text>
              ) : null}
            </Box>
          </Box>
        </Box>
      ) : (
        <TaskHeaderMobile task={task} />
      )}
      {/** Task meta info */}
      {Platform.OS === 'web' ? (
        <Box
          flexDirection='row'
          marginHorizontal='l'
          justifyContent='space-between'>
          <Box flexDirection='row'>
            <Box justifyContent='center' mx='m'>
              {task?.creator?.avatar?.url ? (
                <Image
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 30,
                  }}
                  source={{ uri: task?.creator?.avatar?.url }}
                />
              ) : (
                <Avatar
                  onPress={() => {}}
                  id={task?.creator?.id}
                  avatar={null}
                  label={task?.creator?.name}
                  size='s'
                />
              )}
            </Box>
            <Box>
              <Text
                variant='labelSmall'
                color='textPrimary'
                fontWeight='700'
                fontSize={14}>
                {t('models:projects.report.overview.projectDescription.owner')}
              </Text>
              <Text
                variant='labelSmall'
                color='textPrimary'
                fontWeight='500'
                fontSize={14}>
                {task?.creator?.name}
              </Text>
            </Box>
          </Box>
          <Box marginHorizontal='m' style={styles.line}></Box>
          <Box flexDirection='row'>
            <Box>
              <Text
                variant='labelSmall'
                color='textPrimary'
                fontWeight='700'
                fontSize={14}>
                {t('models:projects.report.overview.taskOverview.startDate')}
              </Text>
              <Text
                variant='labelSmall'
                color='textPrimary'
                fontWeight='500'
                fontSize={14}>
                {format(
                  task?.startDate || '2025-03-12T05:30:00+05:30',
                  'MMM dd, yyyy'
                )}
              </Text>
            </Box>
          </Box>
          <Box marginHorizontal='m' style={styles.line}></Box>
          <Box flexDirection='row'>
            <Box>
              <Text
                variant='labelSmall'
                color='textPrimary'
                fontWeight='700'
                fontSize={14}>
                {t('models:projects.report.overview.taskOverview.dueDate')}
              </Text>
              <Text
                variant='labelSmall'
                color='textPrimary'
                fontWeight='500'
                fontSize={14}>
                {task?.dueDate
                  ? format(
                      task?.dueDate || '2025-03-12T05:30:00+05:30',
                      'MMM dd, yyyy'
                    )
                  : ''}
              </Text>
            </Box>
          </Box>
          <Box marginHorizontal='m' style={styles.line}></Box>
          <Box flexDirection='row'>
            <Box>
              <Text
                variant='labelSmall'
                color='textPrimary'
                fontWeight='700'
                fontSize={14}>
                {t('models:projects.report.overview.taskOverview.status')}
              </Text>
              <Text
                variant='labelSmall'
                color='textPrimary'
                fontWeight='500'
                fontSize={14}>
                {getStatus(task?.status ?? '') ?? ''}
              </Text>
            </Box>
          </Box>
        </Box>
      ) : (
        <TaskDetailMobile t={t} task={task} />
      )}
      <Box marginVertical='l'>
        <Text
          variant='heading1'
          color='textPrimary'
          fontWeight='700'
          fontSize={18}
          textAlign='left'>
          Task Description
        </Text>
        <Text
          variant='webBody'
          color='textPrimary'
          fontWeight='400'
          fontSize={16}
          textAlign='left'
          marginVertical='xs'>
          {task?.description}
        </Text>
      </Box>
      {/** Activity Log */}
      <Box marginHorizontal='s'>
        <Text
          variant='labelLarge'
          color='textPrimary'
          fontWeight='700'
          fontSize={18}
          marginVertical='s'
          textAlign='left'>
          {t('models:projects.report.overview.taskOverview.activityLog')}
        </Text>

        {/* Header Row */}
        <Box marginHorizontal={Platform.OS === 'web' ? 'xl' : 's'}>
          <Box
            marginHorizontal={Platform.OS !== 'web' ? 'm' : 'none'}
            flexDirection='row'
            justifyContent={
              Platform.OS !== 'web' ? 'space-between' : 'flex-start'
            }
            style={styles.headerRow}>
            <Box style={styles.memberColumn}>
              <Text color='grey04' fontWeight='500' fontSize={10}>
                {t(
                  'models:projects.report.overview.taskOverview.member'
                ).toLocaleUpperCase()}
              </Text>
            </Box>
            <Box style={styles.dateColumn}>
              <Text color='grey04' fontWeight='500' fontSize={10}>
                {t(
                  'models:projects.report.overview.taskOverview.date'
                ).toLocaleUpperCase()}
              </Text>
            </Box>
            {Platform.OS === 'web' && (
              <Box style={styles.activityColumn}>
                <Text color='grey04' fontWeight='500' fontSize={10}>
                  {t(
                    'models:projects.report.overview.taskOverview.activity'
                  ).toLocaleUpperCase()}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          marginLeft='s'
          maxWidth={1200}
          width='100%'
          style={styles.horizonalLine}></Box>
      </Box>

      {/** Activity Logs data */}
      {taskActivityLogs?.map((item) => {
        if ((item as { task_id: string })?.task_id === task?.id) {
          return (item as { logs: LogTypes[] })?.logs?.map((log: LogTypes) => {
            const role = getRole(log);

            return (
              <Box key={task?.id}>
                {Platform.OS === 'web' ? (
                  <Box marginHorizontal='l'>
                    <Box style={styles.dataRow}>
                      <Box style={styles.memberColumn}>
                        <Box flexDirection='row' alignItems='center'>
                          <Box style={styles.circle} />
                          <Text
                            variant='body'
                            color='textPrimary'
                            fontWeight='700'
                            fontSize={16}
                            marginHorizontal='s'
                            style={styles.nameText}
                            numberOfLines={2}>
                            {log?.user?.name ?? ''}
                          </Text>
                        </Box>
                      </Box>
                      <Box style={styles.dateColumn}>
                        <Text
                          variant='body'
                          color='textPrimary'
                          fontWeight='400'
                          fontSize={16}>
                          {format(
                            log?.createdAt || '2025-03-12T05:30:00+05:30',
                            'MMM dd, yyyy'
                          )}
                        </Text>
                      </Box>
                      <Box style={styles.activityColumn}>
                        <Text
                          variant='body'
                          color='textPrimary'
                          fontWeight='400'
                          fontSize={16}>
                          {getProjectActivityMessage(log?.event, '', role)
                            ?.message !== ''
                            ? getProjectActivityMessage(log?.event, '', role)
                                ?.message
                            : log?.attachment?.body}
                        </Text>
                      </Box>
                    </Box>
                    <Box width='100%' style={styles.horizonalLine}></Box>
                  </Box>
                ) : (
                  <ActivityLogMobile log={log} role={role} />
                )}
              </Box>
            );
          });
        }

        return null;
      })}
      {/** Activity Logs Photos */}
      <Box marginHorizontal={Platform.OS === 'web' ? 'xl' : 's'}>
        {taskActivityLogs?.map(
          (item) =>
            item?.containPhotos &&
            item?.task_id === task?.id && (
              <Box key={task?.id}>
                <Text
                  variant='heading1'
                  color='textPrimary'
                  fontWeight='700'
                  fontSize={18}
                  marginVertical='l'
                  textAlign='left'>
                  {t('models:projects.report.overview.taskOverview.photos')}
                </Text>
              </Box>
            )
        )}
        <Box flexDirection='row' justifyContent='space-around' flexWrap='wrap'>
          {/** Img source */}
          {taskActivityLogs?.map((item) => {
            if (item?.task_id === task?.id)
              return (
                <>
                  {item?.logs?.map(
                    (log: {
                      attachment: {
                        file: {
                          path: any;
                          thumbnail: string;
                          cdnBaseUrl: any;
                          url: '';
                        };
                        attachments: Array<unknown>[];
                        contentType: string | string[];
                        createdAt: '';
                        body: '';
                      };
                      user: { name: '' };
                    }) => {
                      if (
                        (log?.attachment?.file?.url ||
                          log?.attachment?.attachments?.[0]?.file?.url) &&
                        (log?.attachment?.contentType?.includes('image') ||
                          log?.attachment?.attachments?.[0]?.contentType?.includes(
                            'image'
                          ))
                      ) {
                        return (
                          <Box margin='s' key={task?.id}>
                            <Box>
                              <Box
                                flexDirection={
                                  Platform.OS === 'web' ? 'row' : undefined
                                }>
                                {!!log?.attachment?.attachments?.length &&
                                  log?.attachment?.attachments?.map((images) =>
                                    imagesComponent(images, log)
                                  )}
                              </Box>
                              {!!log?.attachment?.file?.url && (
                                <TouchableOpacity
                                  onPress={() => {
                                    Linking.openURL(
                                      `${log?.attachment?.file?.cdnBaseUrl}/${log?.attachment?.file?.path}`
                                    );
                                  }}>
                                  <Box
                                    margin={
                                      Platform.OS === 'web' ? 'm' : 'none'
                                    }>
                                    <Image
                                      style={{
                                        height: 200,
                                        width: 330,
                                        borderTopLeftRadius: 12,
                                        borderTopRightRadius: 12,
                                      }}
                                      source={{
                                        uri:
                                          log?.attachment?.file?.thumbnail ??
                                          `${log?.attachment?.file?.cdnBaseUrl}/${log?.attachment?.file?.path}`,
                                      }}
                                    />
                                    <Box
                                      height='auto'
                                      width={330}
                                      paddingVertical='xxs'
                                      paddingHorizontal='s'
                                      marginBottom='s'
                                      style={{
                                        backgroundColor: '#F7F8FA',
                                        borderBottomLeftRadius: 12,
                                        borderBottomRightRadius: 12,
                                      }}>
                                      <Box
                                        flexDirection='row'
                                        justifyContent='space-between'>
                                        <Text
                                          variant='labelSmall'
                                          fontSize={14}
                                          marginRight='xxs'
                                          fontWeight='500'>
                                          {log?.user?.name ?? ''}
                                        </Text>
                                        <Text
                                          variant='labelSmall'
                                          fontSize={14}
                                          fontWeight='500'
                                          style={{
                                            color: 'grey04',
                                            opacity: 0.5,
                                          }}>
                                          {`${format(
                                            log?.attachment?.createdAt ??
                                              '2025-03-12T05:30:00+05:30',
                                            'MMM dd, yyyy'
                                          )} at ${format(
                                            log?.attachment?.createdAt ??
                                              '2025-03-12T05:30:00+05:30',
                                            'hh:mm aa'
                                          )}`}
                                        </Text>
                                      </Box>
                                      {log?.attachment?.body ? (
                                        <Text fontSize={14} fontWeight='700'>
                                          {log?.attachment?.body}
                                        </Text>
                                      ) : null}
                                    </Box>
                                  </Box>
                                </TouchableOpacity>
                              )}
                            </Box>
                          </Box>
                        );
                      }
                    }
                  )}
                </>
              );
          })}
        </Box>
      </Box>
    </Box>
  );

const TaskItemComponent = createTaskItem({
  getRole,
  imagesComponent: ImagesComponent,
});

export const TaskDetail = ({ tasks = {} as TasksProp, uuid = '' }) => {
  const { t } = useTranslation();

  const allTaskIds = tasks?.listProjectReportTasks
    ? [...new Set(tasks?.listProjectReportTasks?.map((task) => task?.id))]
    : [];

  const {
    activityLogs: taskActivityLogs = [] as {
      task_id: string;
      logs: LogTypes[];
      containPhotos?: boolean;
    }[],
    loading,
    error,
  } = useActivityLogsForTasks(uuid, allTaskIds);

  if (loading) {
    return (
      <Box flex={1} justifyContent='center' alignContent='center'>
        <ActivityIndicator
          testID='ActivityIndicator'
          size='large'
          color={theme.colors.black}
        />
      </Box>
    );
  }

  if (error) {
    return null;
  }

  return (
    <Box flex={1}>
      {tasks?.listProjectReportTasks?.map((task) => (
        <TaskItemComponent
          task={task}
          taskActivityLogs={taskActivityLogs}
          key={task?.id}
          t={t}
        />
      ))}
    </Box>
  );
};

const styles = StyleSheet.create({
  line: {
    width: 1,
    height: '100%',
    backgroundColor: '#E8E8E8',
  },
  horizonalLine: {
    height: 1,
    alignSelf: Platform.OS === 'web' ? undefined : 'center',
    width:
      Platform.OS === 'web'
        ? Dimensions.get('window').width - 80
        : Dimensions.get('window').width - 60,
    maxWidth: 1200,
    backgroundColor: '#E8E8E8',
  },
  circle: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#18A87D',
  },
  headerRow: {
    paddingVertical: 4,
    alignItems: 'center',
  },
  dataRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    alignItems: 'flex-start',
  },
  memberColumn: {
    width: Platform.OS === 'web' ? '25%' : '40%',
    paddingRight: 8,
  },
  dateColumn: {
    width: Platform.OS === 'web' ? '25%' : '20%',
    paddingRight: 8,
  },
  activityColumn: {
    width: '50%',
    flexShrink: 1,
  },
  nameText: {
    flexShrink: 1,
    flexWrap: 'wrap',
  },

  title: {
    fontSize: 20,
    marginBottom: 20,
  },
  button: {
    alignSelf: 'center',
    padding: 10,
    borderRadius: 5,
    color: 'red',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
});
